import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import './pages.css';

interface ComparisonFile {
  filename: string;
  size: number;
  created: string;
  modified: string;
}

const Comparison: React.FC = () => {
  const [perspective, setPerspective] = useState<'my' | 'client'>('my');
  const [topFile, setTopFile] = useState<File | null>(null);
  const [bottomFiles, setBottomFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // My Perspective fields
  const [ursFile, setUrsFile] = useState<File | null>(null);
  const [clientUrsFiles, setClientUrsFiles] = useState<File[]>([]);
  const [numClientRfs, setNumClientRfs] = useState<number>(1);
  const [clientRfs, setClientRfs] = useState<{ [key: number]: string }>({ 1: '' });
  
  // Client's Perspective fields
  const [selectedComparisons, setSelectedComparisons] = useState<string[]>([]);
  // Client URS->URS | Specification
  const [clientUrsFile, setClientUrsFile] = useState<File | null>(null);
  const [ursOrSpecFiles, setUrsOrSpecFiles] = useState<File[]>([]);
  // Client RF->URS | Specification
  const [clientRfText, setClientRfText] = useState<string>('');
  const [ursOrSpecFilesForRf, setUrsOrSpecFilesForRf] = useState<File[]>([]);
  
  // File management
  const [availableFiles, setAvailableFiles] = useState<ComparisonFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [loadingFiles, setLoadingFiles] = useState(false);

  const loadAvailableFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const response = await apiClient.get('/comparison/files/');
      setAvailableFiles(response.data.files || []);
    } catch (err: any) {
      console.error('Error loading files:', err);
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  useEffect(() => {
    loadAvailableFiles();
  }, [loadAvailableFiles]);

  const handleTopFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setTopFile(e.target.files[0]);
      setError(null);
      setSuccess(false);
    }
  };

  const handleBottomFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setBottomFiles(Array.from(e.target.files));
      setError(null);
      setSuccess(false);
    }
  };

  const handleUrsFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUrsFile(e.target.files[0]);
      setError(null);
      setSuccess(false);
    }
  };

  const handleClientUrsFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setClientUrsFiles(Array.from(e.target.files));
      setError(null);
      setSuccess(false);
    }
  };

  const handleNumClientRfsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const num = parseInt(e.target.value) || 1;
    if (num < 1) {
      setNumClientRfs(1);
      return;
    }
    setNumClientRfs(num);
    
    // Update clientRfs object to match the new number
    const newClientRfs: { [key: number]: string } = {};
    for (let i = 1; i <= num; i++) {
      newClientRfs[i] = clientRfs[i] || '';
    }
    setClientRfs(newClientRfs);
  };

  const handleClientRfChange = (index: number, value: string) => {
    setClientRfs({ ...clientRfs, [index]: value });
  };

  const handleComparisonCheckboxChange = (comparisonType: string, checked: boolean) => {
    if (checked) {
      setSelectedComparisons([...selectedComparisons, comparisonType]);
    } else {
      setSelectedComparisons(selectedComparisons.filter(c => c !== comparisonType));
    }
  };

  const handleClientUrsFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setClientUrsFile(e.target.files[0]);
      setError(null);
      setSuccess(false);
    }
  };

  const handleUrsOrSpecFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setUrsOrSpecFiles(Array.from(e.target.files));
      setError(null);
      setSuccess(false);
    }
  };

  const handleClientRfTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setClientRfText(e.target.value);
    setError(null);
    setSuccess(false);
  };

  const handleUrsOrSpecFilesForRfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setUrsOrSpecFilesForRf(Array.from(e.target.files));
      setError(null);
      setSuccess(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (perspective === 'my') {
      if (!ursFile) {
        setError('Please select a URS file');
        return;
      }
      
      if (numClientRfs < 1) {
        setError('Please specify at least one Client Required Feature');
        return;
      }
      
      // Check that all text areas from 1 to numClientRfs are filled
      const allRfsFilled = Array.from({ length: numClientRfs }, (_, i) => i + 1)
        .every(index => clientRfs[index] && clientRfs[index].trim());
      
      if (clientUrsFiles.length === 0 && !allRfsFilled) {
        setError('Please provide at least one Client URS file or fill all Client Required Feature fields');
        return;
      }
    } else {
      if (selectedComparisons.length === 0) {
        setError('Please select at least one comparison type');
        return;
      }
      
      if (selectedComparisons.includes('client_urs')) {
        if (!clientUrsFile) {
          setError('Please select a client URS file for Client URS→URS | Specification');
          return;
        }
        if (ursOrSpecFiles.length === 0) {
          setError('Please select at least one URS or Specification file for Client URS→URS | Specification');
          return;
        }
      }
      
      if (selectedComparisons.includes('client_rf')) {
        if (!clientRfText.trim()) {
          setError('Please enter Client RF text for Client RF→URS | Specification');
          return;
        }
        if (ursOrSpecFilesForRf.length === 0) {
          setError('Please select at least one URS or Specification file for Client RF→URS | Specification');
          return;
        }
      }
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    const formData = new FormData();
    formData.append('perspective', perspective);
    
    if (perspective === 'my') {
      formData.append('urs_file', ursFile!);
      
      clientUrsFiles.forEach((file) => {
        formData.append('client_urs_files', file);
      });
      
      // Add Client RFs
      Object.entries(clientRfs).forEach(([index, rf]) => {
        if (rf.trim()) {
          formData.append(`client_rf_${index}`, rf.trim());
        }
      });
      
      formData.append('num_client_rfs', numClientRfs.toString());
    } else {
      // Client's Perspective
      formData.append('selected_comparisons', JSON.stringify(selectedComparisons));
      
      if (selectedComparisons.includes('client_urs')) {
        if (clientUrsFile) {
          formData.append('client_urs_file', clientUrsFile);
        }
        ursOrSpecFiles.forEach((file) => {
          formData.append('urs_or_spec_files', file);
        });
      }
      
      if (selectedComparisons.includes('client_rf')) {
        formData.append('client_rf_text', clientRfText);
        ursOrSpecFilesForRf.forEach((file) => {
          formData.append('urs_or_spec_files_for_rf', file);
        });
      }
    }

    try {
      await apiClient.post('/comparison/compare/', formData);
      
      // Reload available files after successful comparison
      await loadAvailableFiles();
      
      setSuccess(true);
    } catch (err: any) {
      const statusCode = err.response?.status;
      const errorData = err.response?.data;

      // Handle 413 error (file too large) - can come from nginx or backend
      const is413Error = statusCode === 413 ||
        err.message?.includes('413') ||
        (typeof errorData === 'string' && errorData.includes('413'));

      if (is413Error) {
        setError('The files you selected are too large to upload. The total maximum files size is 300 MB.  Please try with fewer files or reduce the file sizes before uploading.');
      } else {
        setError(err.response?.data?.error || 'An error occurred during comparison');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedFile) {
      setError('Please select a file to download');
      return;
    }

    try {
      const response = await apiClient.get(`/comparison/files/${selectedFile}`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', selectedFile);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.error || 'An error occurred while downloading the file');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="page-container">
      <h1 className="page-title">URS Comparison Tool</h1>
      <div className="page-description">
        <p>
          This tool enables you to compare different requirement sets (URS vs. Client RF, etc.) and download results.
        </p>
        <h3>How to Use:</h3>
        <ol>
          <li><strong>Select Perspective:</strong> Choose between "My Perspective" or "Client's Perspective".</li>
          <li><strong>For My Perspective:</strong> Upload URS file, multiple Client URS files, and/or enter Client Required Features.</li>
          <li><strong>For Client's Perspective:</strong> Upload reference file and comparison files.</li>
          <li><strong>Click Execute Comparison</strong> to run the comparison.</li>
          <li><strong>Select and download</strong> the generated Excel file from the list below.</li>
        </ol>
        <p className="margin-top-medium">
          <strong>Legend:</strong>
        </p>
        <ul>
          <li>📘: Reference file for URS or RF. It will be compared against other URSs/RFs/Specifications</li>
          <li>📄: URS/RF/Specification used to find matching requirements for the reference file.</li>
        </ul>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="upload-section">
          <label className="upload-label">Choose Perspective:</label>
          <div className="margin-top-small">
            <label className="radio-label">
              <input
                type="radio"
                value="my"
                checked={perspective === 'my'}
                onChange={(e) => setPerspective(e.target.value as 'my' | 'client')}
                disabled={loading}
              />
              {' '}My Perspective
            </label>
            <label>
              <input
                type="radio"
                value="client"
                checked={perspective === 'client'}
                onChange={(e) => setPerspective(e.target.value as 'my' | 'client')}
                disabled={loading}
              />
              {' '}Client's Perspective
            </label>
          </div>
        </div>

        {perspective === 'my' ? (
          <div className="collapsible-section">
            <h2 className="section-title-h3">URS → Client URS | Client RF</h2>
            
            <div className="upload-section">
              <label className="upload-label" htmlFor="ursFile">
                Upload URS (xlsx)
              </label>
              <input
                id="ursFile"
                type="file"
                accept=".xlsx"
                onChange={handleUrsFileChange}
                className="file-input"
                disabled={loading}
              />
            </div>

            <div className="upload-section">
              <label className="upload-label" htmlFor="clientUrsFiles">
                Upload multiple Client URS (xlsx)
              </label>
              <input
                id="clientUrsFiles"
                type="file"
                accept=".xlsx"
                multiple
                onChange={handleClientUrsFilesChange}
                className="file-input"
                disabled={loading}
              />
              {clientUrsFiles.length > 0 && (
                <p className="text-secondary-margin">
                  Selected {clientUrsFiles.length} file(s)
                </p>
              )}
            </div>

            <div className="upload-section">
              <label className="upload-label" htmlFor="numClientRfs">
                Upload Client RFs - Number of Client Required Features
              </label>
              <input
                id="numClientRfs"
                type="number"
                min="1"
                value={numClientRfs}
                onChange={handleNumClientRfsChange}
                className="file-input file-input-narrow"
                disabled={loading}
              />
            </div>

            {Array.from({ length: numClientRfs }, (_, i) => i + 1).map((index) => (
              <div key={index} className="upload-section">
                <label className="upload-label" htmlFor={`clientRf${index}`}>
                  Client Required Feature {index}
                </label>
                <textarea
                  id={`clientRf${index}`}
                  value={clientRfs[index] || ''}
                  onChange={(e) => handleClientRfChange(index, e.target.value)}
                  className="file-input"
                  rows={5}
                  placeholder={`Enter Client Required Feature ${index} here...`}
                  disabled={loading}
                />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="upload-section">
              <label className="upload-label">Select comparisons:</label>
              <div className="margin-top-small">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedComparisons.includes('client_urs')}
                    onChange={(e) => handleComparisonCheckboxChange('client_urs', e.target.checked)}
                    disabled={loading}
                    className="checkbox-input"
                  />
                  Client URS→URS | Specification
                </label>
                <label className="checkbox-label-inline">
                  <input
                    type="checkbox"
                    checked={selectedComparisons.includes('client_rf')}
                    onChange={(e) => handleComparisonCheckboxChange('client_rf', e.target.checked)}
                    disabled={loading}
                    className="checkbox-input"
                  />
                  Client RF→URS | Specification
                </label>
              </div>
            </div>

            {selectedComparisons.includes('client_urs') && (
              <div className="collapsible-section-small">
                <h3 className="section-title-h3">Client URS→URS | Specification</h3>
                
                <div className="upload-section">
                  <label className="upload-label" htmlFor="clientUrsFile">
                    Upload client URS (Xlsx)
                  </label>
                  <input
                    id="clientUrsFile"
                    type="file"
                    accept=".xlsx"
                    onChange={handleClientUrsFileChange}
                    className="file-input"
                    disabled={loading}
                  />
                </div>

                <div className="upload-section">
                  <label className="upload-label" htmlFor="ursOrSpecFiles">
                    Upload multiple URS or Specification (Xlsx Pdf Docx)
                  </label>
                  <input
                    id="ursOrSpecFiles"
                    type="file"
                    accept=".xlsx,.pdf,.docx"
                    multiple
                    onChange={handleUrsOrSpecFilesChange}
                    className="file-input"
                    disabled={loading}
                  />
                  {ursOrSpecFiles.length > 0 && (
                    <p className="text-secondary-margin">
                      Selected {ursOrSpecFiles.length} file(s)
                    </p>
                  )}
                </div>
              </div>
            )}

            {selectedComparisons.includes('client_rf') && (
              <div className="collapsible-section-small">
                <h3 className="section-title-h3">Client RF→URS | Specification</h3>
                
                <div className="upload-section">
                  <label className="upload-label" htmlFor="clientRfText">
                    Enter Client RF (Text)
                  </label>
                  <textarea
                    id="clientRfText"
                    value={clientRfText}
                    onChange={handleClientRfTextChange}
                    className="file-input"
                    rows={10}
                    placeholder="Enter your Client RF here..."
                    disabled={loading}
                  />
                </div>

                <div className="upload-section">
                  <label className="upload-label" htmlFor="ursOrSpecFilesForRf">
                    Upload multiple URS or Specification (Xlsx Pdf Docx)
                  </label>
                  <input
                    id="ursOrSpecFilesForRf"
                    type="file"
                    accept=".xlsx,.pdf,.docx"
                    multiple
                    onChange={handleUrsOrSpecFilesForRfChange}
                    className="file-input"
                    disabled={loading}
                  />
                  {ursOrSpecFilesForRf.length > 0 && (
                    <p className="text-secondary-margin">
                      Selected {ursOrSpecFilesForRf.length} file(s)
                    </p>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">Comparison completed successfully! Select a file from the list below to download.</div>}

        <button 
          type="submit" 
          className="button button-submit-spaced"
          disabled={
            loading || 
            (perspective === 'my' 
              ? (() => {
                  if (!ursFile || numClientRfs < 1) return true;
                  // Check that all text areas from 1 to numClientRfs are filled
                  const allRfsFilled = Array.from({ length: numClientRfs }, (_, i) => i + 1)
                    .every(index => clientRfs[index] && clientRfs[index].trim());
                  return clientUrsFiles.length === 0 && !allRfsFilled;
                })()
              : (() => {
                  if (selectedComparisons.length === 0) return true;
                  let isValid = true;
                  if (selectedComparisons.includes('client_urs')) {
                    isValid = isValid && clientUrsFile !== null && ursOrSpecFiles.length > 0;
                  }
                  if (selectedComparisons.includes('client_rf')) {
                    isValid = isValid && clientRfText.trim() !== '' && ursOrSpecFilesForRf.length > 0;
                  }
                  return !isValid;
                })())
          }
        >
          {loading ? 'Processing...' : 'Execute Comparison'}
        </button>
      </form>

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Processing your comparison...</p>
        </div>
      )}

      <div className="results-section">
        <h2 className="results-title">Available Comparison Results</h2>
        
        {loadingFiles ? (
          <p>Loading files...</p>
        ) : availableFiles.length === 0 ? (
          <p className="text-secondary">No comparison results available yet.</p>
        ) : (
          <>
            <div className="upload-section">
              <label className="upload-label" htmlFor="fileSelect">
                Select a file to download
              </label>
              <select
                id="fileSelect"
                value={selectedFile}
                onChange={(e) => setSelectedFile(e.target.value)}
                className="file-input select-margin-bottom"
              >
                <option value="">Select a file...</option>
                {availableFiles.map((file) => (
                  <option key={file.filename} value={file.filename}>
                    {file.filename} ({formatFileSize(file.size)}) - {formatDate(file.modified)}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="button"
              onClick={handleDownload}
              disabled={!selectedFile}
            >
              Download Selected File
            </button>

            <button
              type="button"
              className="button button-refresh"
              onClick={loadAvailableFiles}
            >
              Refresh List
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Comparison;

